import React, {useCallback, useEffect, useRef, useState, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {
  FaExclamationCircle,
  FaFileAlt,
  FaCheckCircle,
  FaPaperclip,
  FaCommentDots,
  FaTimes,
  FaTrash,
  FaExclamationTriangle,
  FaEye,
  FaPlus,
} from 'react-icons/fa'
import axios from 'axios'
import './../style.css'
import ChatModal from './ChatModal'
import DocumentPreviewModal from './DocumentPreviewModal'
import {toast} from 'react-toastify'
import {useAuth} from '../../../modules/auth'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {createPusherClient} from '../../../../_metronic/helpers/pusherClient'
import {Spinner} from 'react-bootstrap'
import styled from 'styled-components'
import {Modal} from 'react-bootstrap'

interface DocumentsProps {
  claimDetails: any
  onDocumentClick: (docLabel: string) => void
  onDocumentsStatusChange: (status: boolean) => void
  onDocumentValidationChange?: (hasInvalid: boolean) => void
  onDocumentRealtimeUpdate?: (data: any) => void
}

interface ReuploadModalState {
  isOpen: boolean
  docKey: number | null
  fileIndex: number | null
  documentLabel: string
  position: {top: number; left: number} | null
}

const ProgressBar = styled.div<{$progress: number}>`
  width: ${(props) => props.$progress}%;
  height: 8px;
  background: linear-gradient(90deg, #059bb6 0%, #05b6a6 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
`

const Documents: React.FC<DocumentsProps> = ({
  claimDetails,
  onDocumentClick,
  onDocumentsStatusChange,
  onDocumentValidationChange,
  onDocumentRealtimeUpdate,
}) => {
  const user = useAuth()
  const {auth, currentUser} = useAuth()

  // Check if provider country is not Indonesia
  const isNotIndonesia = currentUser?.providerCountry?.toUpperCase() !== 'INDONESIA'
  const {formatMessage: f} = useIntl()
  const [documentFiles, setDocumentFiles] = useState<Record<string, any[]>>({})
  const [uploadStatus, setUploadStatus] = useState<Record<string, boolean | null>>({})
  const [documentsData, setDocumentsData] = useState<any[]>([])
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false)
  const [chatMessage, setChatMessage] = useState<string>('')
  const [deletingFiles, setDeletingFiles] = useState<Record<string, boolean>>({})
  const [lastToastTime, setLastToastTime] = useState<number>(0)
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [isUploading, setIsUploading] = useState<Record<string, boolean>>({})
  const [uploadingLabel, setUploadingLabel] = useState<string>('')
  const [isLoadingDocuments, setIsLoadingDocuments] = useState<boolean>(false)

  // Mirrors for the Pusher handler to read. The handler both reads and sets these,
  // so keeping them in the Pusher effect's deps rebuilt the client on every file
  // event. Refs give the handler the same committed value without the churn.
  // See docs/plan/IDH-1620.
  const documentFilesRef = useRef<Record<string, any[]>>(documentFiles)
  const lastToastTimeRef = useRef<number>(lastToastTime)
  useEffect(() => {
    documentFilesRef.current = documentFiles
  }, [documentFiles])
  useEffect(() => {
    lastToastTimeRef.current = lastToastTime
  }, [lastToastTime])

  // Track labels uploaded in this session (persisted across page refresh)
  const [recentlyUploadedLabels, setRecentlyUploadedLabels] = useState<Set<string>>(() => {
    try {
      const stored = sessionStorage.getItem('recentlyUploadedLabels')
      return stored ? new Set(JSON.parse(stored)) : new Set()
    } catch {
      return new Set()
    }
  })

  // Preview modal state
  const [previewData, setPreviewData] = useState<{
    isOpen: boolean
    fileName: string
    label: string
  }>({
    isOpen: false,
    fileName: '',
    label: '',
  })

  // Custom labels state
  const claimNumber = claimDetails?.claimDetails?.claim?.claimNumber
  const [customLabels, setCustomLabels] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(`customLabels:${claimNumber}`)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  })
  const [showCustomLabelModal, setShowCustomLabelModal] = useState(false)
  const [newCustomLabelName, setNewCustomLabelName] = useState('')
  const [customLabelFile, setCustomLabelFile] = useState<File | null>(null)

  // Document label mapping between locales to prevent duplicates
  const documentLabelMapping = {
    // English to Indonesian mapping
    'Hospital Admission Form': 'Lembar Medis Awal (LMA)',
    'Copy of Patient ID': 'Copy KTP Pasien',
    'Copy of Insurance Card': 'Copy Kartu Asuransi Pasien',
    'Declaration Letter': 'Surat Pernyataan (SP)',
    // Indonesian to English mapping
    'Lembar Medis Awal (LMA)': 'Hospital Admission Form',
    'Copy KTP Pasien': 'Copy of Patient ID',
    'Copy Kartu Asuransi Pasien': 'Copy of Insurance Card',
    'Surat Pernyataan (SP)': 'Declaration Letter',
    // Backward compatibility: map old BE labels to current
    'Hasil Penunjang Medis': 'Hasil Penunjang',
    'Supporting Document': 'Hasil Penunjang',
  }

  // Function to normalize document label to current locale
  const normalizeDocumentLabel = (label: string): string => {
    // If the label matches current locale's defaultDocuments, return as is
    const currentLabels = defaultDocuments.map((doc) => doc.label)
    if (currentLabels.includes(label)) {
      return label
    }

    // If the label is from different locale, map it to current locale
    if (documentLabelMapping[label]) {
      return documentLabelMapping[label]
    }

    // If no mapping found, return original label
    return label
  }

  // Memoize defaultDocuments to update when locale changes
  const defaultDocuments = useMemo(() => {
    const docs = [
      {label: f({id: 'CLAIM.INPATIENT.INITIAL_MEDICAL_SHEET'}), key: 0},
      {label: f({id: 'CLAIM.INPATIENT.PATIENT_ID_COPY'}), key: 1},
      {label: f({id: 'CLAIM.INPATIENT.INSURANCE_CARD_COPY'}), key: 2},
      {label: f({id: 'CLAIM.INPATIENT.STATEMENT_LETTER'}), key: 3},
      // {label: f({id: 'CLAIM.INPATIENT.MEDICAL_INQUIRY'}), key: 4},
      // {label: f({id: 'CLAIM.INPATIENT.LAB_RESULT'}), key: 5},
    ]

    // Add Authorization of Benefit document only for non-Indonesia providers
    if (isNotIndonesia) {
      docs.push({label: f({id: 'CLAIM.INPATIENT.AUTHORIZATION_OF_BENEFIT'}), key: 4})
    }

    return docs
  }, [f, isNotIndonesia])

  useEffect(() => {
    const initialFiles: Record<string, any[]> = {}
    defaultDocuments.forEach((doc) => {
      initialFiles[doc.label] = []
    })
    setDocumentFiles(initialFiles)
  }, [])

  const checkAllDocumentsUploaded = useCallback(() => {
    // Get all unique document labels from both default and API response
    const allDocumentLabels = new Set([
      ...defaultDocuments.map((doc) => doc.label),
      ...Object.keys(documentFiles),
    ])

    const allUploaded = Array.from(allDocumentLabels).every((label) => {
      // Skip Prescription documents
      if (label === 'Prescription') return true

      const files = documentFiles[label]
      return (
        files &&
        files.length > 0 &&
        // Consider files valid if they don't have INVALID or Request Document status
        !files.some((file) => file.status === 'INVALID' || file.status === 'Request Document')
      )
    })

    onDocumentsStatusChange(allUploaded)
  }, [documentFiles, onDocumentsStatusChange, defaultDocuments])

  useEffect(() => {
    checkAllDocumentsUploaded()
  }, [documentFiles, checkAllDocumentsUploaded])

  /**
   * ListFiles can take many seconds (IDH-1619 — svc.claim awaits a Pusher publish
   * inside the GET). Until it returns, the rows below would otherwise read as
   * "no file uploaded" with a live Upload button, which invites a duplicate
   * upload. Pass `background: true` to refresh without showing the indicator.
   */
  const fetchDocuments = async ({background = false}: {background?: boolean} = {}) => {
    const claimNumber = claimDetails.claimDetails.claim.claimNumber
    // Use the apiEndpoint from claimDetails if provided, otherwise use the default endpoint
    const apiUrl =
      claimDetails.apiEndpoint ||
      `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ListFiles/${claimNumber}`
    const headers = {
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }

    if (!background) setIsLoadingDocuments(true)

    try {
      const response = await axios.get(apiUrl, {headers})
      if (response.data.isSuccess) {
        const apiDocuments = response.data.data.filter((doc: any) => doc.label !== 'Prescription')

        // Normalize API document labels to current locale
        const normalizedApiDocuments = apiDocuments.map((doc: any) => ({
          ...doc,
          label: normalizeDocumentLabel(doc.label),
        }))

        // Check for invalid documents across all documents
        const hasInvalidDocuments = normalizedApiDocuments.some(
          (doc: any) =>
            doc.document &&
            doc.document.length > 0 &&
            doc.document.some(
              (file: any) => file.status === 'INVALID' || file.status === 'Request Document'
            )
        )

        // Check if all required documents are uploaded
        const allDocumentsUploaded = defaultDocuments.every((defaultDoc) =>
          normalizedApiDocuments.some(
            (apiDoc) =>
              apiDoc.label === defaultDoc.label && apiDoc.document && apiDoc.document.length > 0
          )
        )

        // Create a map of all unique documents
        const allDocuments = new Map()

        // Add default documents first
        defaultDocuments.forEach((doc) => {
          allDocuments.set(doc.label, {...doc, key: doc.key})
        })

        // Add or update with normalized API documents
        normalizedApiDocuments.forEach((doc: any, index: number) => {
          if (!allDocuments.has(doc.label)) {
            allDocuments.set(doc.label, {
              label: doc.label,
              key: defaultDocuments.length + index,
              document: doc.document,
            })
          } else {
            const existing = allDocuments.get(doc.label)
            allDocuments.set(doc.label, {
              ...existing,
              document: doc.document,
            })
          }
        })

        // Convert map back to array
        const finalDocuments = Array.from(allDocuments.values())
        console.log('finalDocuments: ', finalDocuments)

        // Create updated files object
        const updatedFiles: Record<string, any[]> = {}
        finalDocuments.forEach((doc: any) => {
          if (doc.document) {
            updatedFiles[doc.label] = doc.document.map((file: any) => ({
              name: file.name,
              id: file.id,
              type: file.name.split('.').pop(),
              status: file.status,
              isArchive: file.isArchive,
            }))
          } else {
            updatedFiles[doc.label] = []
          }
        })

        setDocumentsData(finalDocuments)
        setDocumentFiles(updatedFiles)

        // Update parent components
        const allDocumentsValid = !hasInvalidDocuments && allDocumentsUploaded
        onDocumentsStatusChange(allDocumentsValid)
        if (onDocumentValidationChange) {
          onDocumentValidationChange(!allDocumentsValid)
        }

        // Check if all documents have IDLE status
        const allDocumentsIdle = normalizedApiDocuments.every(
          (doc: any) =>
            doc.document &&
            doc.document.length > 0 &&
            doc.document.every((file: any) => file.status === 'IDLE')
        )

        // If all documents are uploaded and have IDLE status, enable the button
        if (allDocumentsUploaded && allDocumentsIdle) {
          onDocumentsStatusChange(true)
          if (onDocumentValidationChange) {
            onDocumentValidationChange(false)
          }
        }
      }
    } catch (error) {
      console.error('Error fetching documents:', error)
      toast.error('Failed to fetch documents. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
      })
    } finally {
      if (!background) setIsLoadingDocuments(false)
    }
  }

  useEffect(() => {
    fetchDocuments()
  }, [claimDetails])

  // Refetch documents when locale changes to ensure proper label normalization
  useEffect(() => {
    if (claimDetails) {
      fetchDocuments()
    }
  }, [defaultDocuments])

  useEffect(() => {
    console.log('Token in useEffect:', user)
  }, [user])

  useEffect(() => {
    const pusher = createPusherClient('InpatientDocs')

    const claimNumber = claimDetails.claimDetails.claim.claimNumber
    const fileStatusChannel = pusher.subscribe(`file-channel-${claimNumber}`)
    const listFileChannel = pusher.subscribe(`list-file-channel-${claimNumber}`)

    fileStatusChannel.bind(`get-${claimNumber}`, (data: any) => {
      if (data && data.message) {
        const {message} = data
        const {Label, Status, Id, DocumentName, isArchive, UpdatedBy} = message

        // Normalize the incoming label to current locale
        const normalizedLabel = normalizeDocumentLabel(Label)

        // Update document files state immediately
        setDocumentFiles((prevFiles) => {
          const newFiles = {...prevFiles}
          if (newFiles[normalizedLabel]) {
            newFiles[normalizedLabel] = [
              {
                name: DocumentName,
                id: Id,
                type: DocumentName.split('.').pop(),
                status: Status,
                isArchive: isArchive,
              },
            ]
          }
          return newFiles
        })

        // Update documents data state
        setDocumentsData((prevData) => {
          return prevData.map((doc) => {
            if (doc.label === normalizedLabel) {
              return {
                ...doc,
                document: [
                  {
                    name: DocumentName,
                    id: Id,
                    status: Status,
                    isArchive: isArchive,
                  },
                ],
              }
            }
            return doc
          })
        })

        // Check for any invalid documents
        const hasInvalidDocuments = Object.values(documentFilesRef.current).some((files) =>
          files.some((file) => file.status === 'INVALID' || file.status === 'Request Document')
        )

        // Check if all required documents are uploaded
        const allDocumentsUploaded = defaultDocuments.every(
          (doc) => documentFilesRef.current[doc.label]?.length > 0
        )

        // Update parent components
        onDocumentsStatusChange(allDocumentsUploaded && !hasInvalidDocuments)
        if (onDocumentValidationChange) {
          onDocumentValidationChange(hasInvalidDocuments)
        }

        // Show toast for invalid documents
        const now = Date.now()
        if (Status === 'INVALID' && now - lastToastTimeRef.current > 5000) {
          toast.error(
            `${normalizedLabel} has been rejected by ${UpdatedBy}. Please review and resubmit.`,
            {
              position: 'top-right',
              autoClose: 3000,
            }
          )
          setLastToastTime(now)
        }
      }
    })

    listFileChannel.bind(`get-list-${claimNumber}`, (data: any) => {
      if (data.isSuccess) {
        // Pass the realtime data up to parent immediately
        if (onDocumentRealtimeUpdate) {
          onDocumentRealtimeUpdate(data)
        }

        const apiDocuments = data.data.filter((doc: any) => doc.label !== 'Prescription')

        // Normalize API document labels to current locale
        const normalizedApiDocuments = apiDocuments.map((doc: any) => ({
          ...doc,
          label: normalizeDocumentLabel(doc.label),
        }))

        // Check for invalid documents
        const hasInvalidDocuments = normalizedApiDocuments.some(
          (doc: any) =>
            doc.document &&
            doc.document.length > 0 &&
            doc.document.some(
              (file: any) => file.status === 'INVALID' || file.status === 'Request Document'
            )
        )

        // Check if all required documents are uploaded
        const allDocumentsUploaded = defaultDocuments.every((defaultDoc) =>
          normalizedApiDocuments.some(
            (apiDoc) =>
              apiDoc.label === defaultDoc.label && apiDoc.document && apiDoc.document.length > 0
          )
        )

        // Check if all documents have acceptable status (not INVALID or Request Document)
        const allDocumentsValidStatus = normalizedApiDocuments.every(
          (doc: any) =>
            doc.document &&
            doc.document.length > 0 &&
            doc.document.every(
              (file: any) => file.status !== 'INVALID' && file.status !== 'Request Document'
            )
        )

        // Calculate final validation state
        const allDocumentsValid =
          allDocumentsUploaded && allDocumentsValidStatus && !hasInvalidDocuments

        // Create maps for easier comparison
        const defaultDocsMap = new Map(defaultDocuments.map((doc) => [doc.label, doc]))
        const apiDocsMap = new Map(normalizedApiDocuments.map((doc: any) => [doc.label, doc]))

        // Determine which set of documents to use based on our strategy
        let finalDocuments: any[] = []

        // Case 1: API count equals Default count - Compare and use API as source of truth
        if (normalizedApiDocuments.length === defaultDocuments.length) {
          finalDocuments = defaultDocuments.map((defaultDoc) => {
            const apiDoc = apiDocsMap.get(defaultDoc.label)
            return apiDoc || defaultDoc
          })
        }
        // Case 2: API count > Default count - Use API data
        else if (normalizedApiDocuments.length > defaultDocuments.length) {
          finalDocuments = normalizedApiDocuments
        }
        // Case 3: Default count > API count - Use Default data but update with available API data
        else {
          finalDocuments = defaultDocuments.map((defaultDoc) => {
            const apiDoc = apiDocsMap.get(defaultDoc.label)
            return apiDoc || defaultDoc
          })
        }

        // Merge custom labels into finalDocuments (union approach)
        const existingLabels = new Set(finalDocuments.map((d: any) => d.label))
        customLabels.forEach((cl) => {
          if (!existingLabels.has(cl)) {
            const apiDoc = apiDocsMap.get(cl)
            finalDocuments.push(
              apiDoc || {
                label: cl,
                key: defaultDocuments.length + normalizedApiDocuments.length,
                document: [],
              }
            )
            existingLabels.add(cl)
          }
        })

        // Create updated files object based on final documents
        const updatedFiles: Record<string, any[]> = {}

        finalDocuments.forEach((doc: any) => {
          // If it's an API document with files
          if (doc.document) {
            updatedFiles[doc.label] = doc.document.map((file: any) => ({
              name: file.name,
              id: file.id,
              type: file.name.split('.').pop(),
              status: file.status,
              isArchive: file.isArchive,
            }))
          }
          // If it's a default document or empty API document
          else {
            updatedFiles[doc.label] = []
          }
        })

        // Notify parent components of status changes immediately
        onDocumentsStatusChange(allDocumentsValid)
        if (onDocumentValidationChange) {
          onDocumentValidationChange(!allDocumentsValid)
        }

        // Update local state after notifying parent
        setDocumentsData(finalDocuments)
        setDocumentFiles(updatedFiles)
      }
    })

    return () => {
      fileStatusChannel.unbind_all()
      pusher.unsubscribe(`file-channel-${claimNumber}`)
      listFileChannel.unbind_all()
      pusher.unsubscribe(`list-file-channel-${claimNumber}`)
      pusher.disconnect()
    }
  }, [claimDetails.claimDetails.claim.claimNumber])

  const status = claimDetails?.claimDetails?.claim?.status

  const [pendingIGLSnapshot, setPendingIGLSnapshot] = useState<string[]>(() => {
    return status === 'Pending on IGL' ? customLabels : []
  })

  useEffect(() => {
    if (status === 'Pending on IGL' && pendingIGLSnapshot.length === 0) {
      setPendingIGLSnapshot(customLabels)
    } else if (status !== 'Pending on IGL' && pendingIGLSnapshot.length > 0) {
      setPendingIGLSnapshot([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  // Modify the isInteractionDisabled function to include Reject and Void statuses
  const isInteractionDisabled = () => {
    const disabledStatuses = [
      'Admission Review',
      'Request IGL',
      'IGL RELEASED',
      'Daily Monitoring',
      'Request FGL',
      'FGL RELEASED',
      'Reject',
      'Void',
      'IGL Released',
      'FGL Released',
      'FGL Review',
      // 'Pending on IGL'
    ]
    // Also disabled while ListFiles is in flight — the current file state is
    // unknown until it returns, so uploading now risks a duplicate.
    return isLoadingDocuments || disabledStatuses.includes(status)
  }

  // Modify handleFileChange to check status first
  const handleFileChange = async (label: string, event: React.ChangeEvent<HTMLInputElement>) => {
    if (isInteractionDisabled()) return

    if (event.target.files && event.target.files.length > 0) {
      const selectedFiles = Array.from(event.target.files)

      // Check file size limit (10MB) BEFORE updating any state
      const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes
      const oversizedFiles = selectedFiles.filter((file) => file.size > MAX_FILE_SIZE)

      if (oversizedFiles.length > 0) {
        const fileNames = oversizedFiles.map((f) => f.name).join(', ')
        toast.error(`File size exceeds maximum allowed size of 10MB: ${fileNames}`, {
          position: 'bottom-right',
          autoClose: 5000,
        })
        // Reset the file input
        event.target.value = ''
        return
      }

      try {
        const currentDoc = documentsData.find((doc) => doc.label === label)
        console.log('currentDoc: ', currentDoc)
        const fileInput = event.target as HTMLInputElement

        // Determine currentFileId - prioritize INVALID or Request Document status
        let currentFileId: number | undefined
        if (fileInput.dataset.replaceFileId) {
          currentFileId = parseInt(fileInput.dataset.replaceFileId)
        } else if (currentDoc?.document && currentDoc.document.length > 0) {
          // First, look for documents with INVALID or Request Document status
          const invalidDoc = currentDoc.document.find(
            (doc) => doc.status === 'INVALID' || doc.status === 'Request Document'
          )
          currentFileId = invalidDoc ? invalidDoc.id : currentDoc.document[0]?.id
        }

        // Immediately update UI to show IDLE status
        setDocumentFiles((prevState) => ({
          ...prevState,
          [label]: [
            {
              name: selectedFiles[0].name || '',
              type: selectedFiles[0].name.split('.').pop() || '',
              status: 'IDLE',
              id: currentFileId || 0,
            },
          ],
        }))

        // Update recently uploaded tracking (persist to sessionStorage)
        setRecentlyUploadedLabels((prev) => {
          const next = new Set(prev).add(label)
          sessionStorage.setItem('recentlyUploadedLabels', JSON.stringify(Array.from(next)))
          return next
        })

        // Also update documentsData for immediate UI reflection
        setDocumentsData((prevData) =>
          prevData.map((doc) => {
            if (doc.label === label) {
              return {
                ...doc,
                document: [
                  {
                    name: selectedFiles[0].name,
                    id: currentFileId || 0,
                    status: 'IDLE',
                    isArchive: false,
                  },
                ],
              }
            }
            return doc
          })
        )

        // Attempt upload
        await uploadFiles(label, selectedFiles, currentFileId)

        // Clear the replaceFileId after upload
        if (fileInput.dataset.replaceFileId) {
          delete fileInput.dataset.replaceFileId
        }
      } catch (error) {
        console.error('Error handling file change:', error)
        toast.error('Failed to upload file. Please try again.', {
          position: 'bottom-right',
          autoClose: 3000,
        })
      }
    }
  }

  const uploadFiles = async (label: string, files: File[], existingFileId?: number) => {
    if (!files || files.length === 0) return

    // Check file size limit (10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE)

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((f) => f.name).join(', ')
      toast.error(`File size exceeds maximum allowed size of 10MB: ${fileNames}`, {
        position: 'bottom-right',
        autoClose: 5000,
      })
      return
    }

    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))

    const claimNumber = claimDetails.claimDetails.claim.claimNumber
    const typeDocument = label

    // Always use the existing file ID if available
    const documentId = existingFileId || 0

    const apiUrl = `${
      process.env.REACT_APP_API_CLAIM_URL
    }/api/Claims/UploadFiles?claimNumber=${claimNumber}&typeDocument=${encodeURIComponent(
      typeDocument
    )}&user=${user.currentUser?.email}&id=${documentId}`

    try {
      setUploadStatus((prevStatus) => ({...prevStatus, [label]: null}))
      setIsUploading((prev) => ({...prev, [label]: true}))
      setUploadingLabel(label)
      setUploadProgress(0)

      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${user.currentUser?.token}`,
        },
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || 0
          const current = progressEvent.loaded
          const percentage = Math.round((current / total) * 100)
          setUploadProgress(percentage)
        },
      })

      if (response.data.isSuccess) {
        // After successful upload, fetch updated document list
        const listResponse = await axios.get(
          `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ListFiles/${claimNumber}`,
          {
            headers: {
              Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
            },
          }
        )

        if (listResponse.data.isSuccess) {
          // Background: the row already shows the optimistic IDLE file, so the
          // foreground indicator would blank the grid the staff just uploaded into
          // (and re-disable the inputs) for as long as ListFiles takes.
          await fetchDocuments({background: true})

          // Notify parent components immediately
          if (onDocumentRealtimeUpdate) {
            console.log('listResponse.data: ', listResponse.data)
            onDocumentRealtimeUpdate(listResponse.data)
          }

          const apiDocuments = listResponse.data.data.filter(
            (doc: any) => doc.label !== 'Prescription'
          )

          // Normalize API document labels to current locale
          const normalizedApiDocuments = apiDocuments.map((doc: any) => ({
            ...doc,
            label: normalizeDocumentLabel(doc.label),
          }))

          // Check document statuses - only consider INVALID and Request Document as problematic
          const hasInvalidDocuments = normalizedApiDocuments.some(
            (doc: any) =>
              doc.document &&
              doc.document.length > 0 &&
              doc.document.some(
                (file: any) => file.status === 'INVALID' || file.status === 'Request Document'
              )
          )

          const allDocumentsUploaded = defaultDocuments.every((defaultDoc) =>
            normalizedApiDocuments.some(
              (apiDoc) =>
                apiDoc.label === defaultDoc.label && apiDoc.document && apiDoc.document.length > 0
            )
          )

          // Check if all documents have acceptable status (VALID, IDLE, or other non-problematic statuses)
          const allDocumentsValid = normalizedApiDocuments.every(
            (doc: any) =>
              doc.document &&
              doc.document.length > 0 &&
              doc.document.every(
                (file: any) => file.status !== 'INVALID' && file.status !== 'Request Document'
              )
          )

          // Update parent components with the latest status
          const documentsReadyForIGL =
            allDocumentsUploaded && !hasInvalidDocuments && allDocumentsValid
          onDocumentsStatusChange(documentsReadyForIGL)
          if (onDocumentValidationChange) {
            onDocumentValidationChange(!documentsReadyForIGL)
          }
        }

        setUploadStatus((prevStatus) => ({...prevStatus, [label]: true}))
        toast.success('File uploaded successfully!', {
          position: 'bottom-right',
          autoClose: 3000,
        })
      } else {
        throw new Error(response.data.message || 'Upload failed')
      }
    } catch (error) {
      console.error('Error uploading files:', error)
      setUploadStatus((prevStatus) => ({...prevStatus, [label]: false}))

      // Ensure document remains empty when upload fails
      setDocumentFiles((prevState) => {
        console.log('prevState: ', prevState)
        return {
          ...prevState,
          [label]: [],
        }
      })

      setDocumentsData((prevData) =>
        prevData.map((doc) => {
          if (doc.label === label) {
            console.log('prevData: ', prevData)
            return {
              ...doc,
              document: undefined,
            }
          }
          return doc
        })
      )

      toast.error('Failed to upload file. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
      })
      throw error
    } finally {
      setIsUploading((prev) => ({...prev, [label]: false}))
      setUploadProgress(0)
    }
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'VALID':
        return 'valid'
      case 'INVALID':
      case 'REQUEST DOCUMENT':
      case 'REQUEST':
        return 'request'
      case 'IDLE':
        return 'idle'
      default:
        return ''
    }
  }

  const getDocumentStatusIcon = (document: any) => {
    if (!document || !document.name) {
      return <FaExclamationCircle className='document-icon warning' />
    }

    switch (document.status) {
      case 'VALID':
      case 'Valid':
        return <FaCheckCircle className='document-icon success' />
      case 'REQUEST':
        return <FaExclamationTriangle className='document-icon request' />
      default:
        return <FaExclamationCircle className='document-icon warning' />
    }
  }

  const getDocumentStatusText = (document: any) => {
    if (!document || !document.name) {
      return 'Document not uploaded'
    }

    switch (document.status) {
      case 'VALID':
        return 'Approved'
      case 'INVALID':
        return 'Revision needed'
      default:
        return 'Uploaded'
    }
  }

  // Check if all default required documents are uploaded (for custom label gate)
  const allRequiredFilled = useMemo(() => {
    return defaultDocuments.every((doc) => {
      const files = documentFiles[doc.label]
      return files && files.length > 0 && files[0]?.name
    })
  }, [defaultDocuments, documentFiles])

  // Save custom labels to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(`customLabels:${claimNumber}`, JSON.stringify(customLabels))
  }, [customLabels, claimNumber])

  // Add custom label document upload
  const handleAddCustomLabel = async () => {
    if (!newCustomLabelName.trim()) {
      toast.warning('Please enter a document name')
      return
    }
    if (!customLabelFile) {
      toast.warning('Please select a file')
      return
    }

    const labelName = newCustomLabelName.trim()

    // Close modal immediately
    setShowCustomLabelModal(false)
    setNewCustomLabelName('')
    setCustomLabelFile(null)

    // Add to custom labels optimistically
    setCustomLabels((prev) => {
      if (prev.includes(labelName)) return prev
      return [...prev, labelName]
    })

    // Upload file in background
    try {
      const formData = new FormData()
      formData.append('files', customLabelFile)

      const apiUrl = `${
        process.env.REACT_APP_API_CLAIM_URL
      }/api/Claims/UploadFiles?claimNumber=${claimNumber}&typeDocument=${encodeURIComponent(
        labelName
      )}&user=${currentUser?.email || 'SYSTEM'}&id=0`

      await axios.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${currentUser?.token}`,
        },
      })

      // Background: the custom label is already listed optimistically.
      fetchDocuments({background: true})
    } catch (err) {
      toast.error(`Failed to upload ${labelName}`)
    }
  }

  // Delete custom label
  const handleDeleteCustomLabel = async (label: string) => {
    const doc = documentsData.find((d) => d.label === label)
    const fileId = doc?.document?.[0]?.id

    if (fileId) {
      try {
        await axios.post(
          `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/Provider/DeleteFile?id=${fileId}&claimNumber=${claimNumber}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${currentUser?.token}`,
            },
          }
        )
      } catch (err) {
        console.error('Error deleting custom label file:', err)
      }
    }

    setCustomLabels((prev) => prev.filter((l) => l !== label))
    setDocumentFiles((prev) => {
      const next = {...prev}
      delete next[label]
      return next
    })
  }

  // Handle reupload header button
  return (
    <>
      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='light'
        style={{zIndex: 9999}}
      />
      <div className='documents-container'>
        <div
          className='section-header'
          style={{gridColumn: '1 / -1', marginTop: '8px', marginBottom: '4px'}}
        >
          <h6 className='fw-bold mb-0' style={{color: '#127289', fontSize: '13px'}}>
            {f({id: 'CLAIM.INPATIENT.REQUIRED_DOCUMENTS'})}
          </h6>
          <hr className='my-1' style={{borderColor: '#127289', opacity: 0.2}} />
        </div>
        {isLoadingDocuments && (
          <div
            className='d-flex align-items-center justify-content-center gap-2 py-4'
            style={{gridColumn: '1 / -1'}}
            role='status'
            aria-live='polite'
          >
            <div className='spinner-border spinner-border-sm text-primary' />
            <span className='text-primary fw-bold' style={{fontSize: '12px'}}>
              Loading documents...
            </span>
          </div>
        )}
        {(isLoadingDocuments ? [] : defaultDocuments)
          .filter((doc) => doc.label !== 'Prescription' && doc.label !== 'Chat Document')
          .map((doc) => {
            const currentDoc = documentFiles[doc.label] || []
            const isRequestDoc =
              currentDoc.length > 0 &&
              currentDoc.some((f) => f.status === 'Request Document' || f.status === 'REQUEST')
            const isInvalid =
              currentDoc.length > 0 && currentDoc.some((f) => f.status === 'INVALID')
            const isValid = currentDoc.length > 0 && currentDoc.some((f) => f.status === 'VALID')
            const isIdle = currentDoc.length > 0 && currentDoc.some((f) => f.status === 'IDLE')
            const isRecentlyUploaded = recentlyUploadedLabels.has(doc.label)

            // Determine if we should show Replace vs Upload
            const showReplace =
              (isInvalid || (isIdle && isRecentlyUploaded)) && !isInteractionDisabled()
            const showUpload = (isRequestDoc || currentDoc.length === 0) && !isInteractionDisabled()

            return (
              <div
                key={doc.key}
                className={`document-item ${isInteractionDisabled() ? 'disabled' : ''}`}
              >
                <div className='document-header'>
                  <div className='document-header-content'>
                    <div
                      className={`document-icon ${
                        isValid ? 'success' : isRequestDoc || isInvalid ? 'request' : 'warning'
                      }`}
                    >
                      {isValid ? (
                        <FaCheckCircle />
                      ) : isRequestDoc || isInvalid ? (
                        <FaExclamationTriangle />
                      ) : (
                        <FaExclamationCircle />
                      )}
                    </div>
                    <div className='d-flex flex-column flex-grow-1'>
                      <span className='document-text'>{doc.label}</span>
                      {isRecentlyUploaded && (
                        <span
                          className='badge rounded-pill mt-1'
                          style={{
                            backgroundColor: 'rgba(5, 155, 182, 0.1)',
                            color: '#059BB6',
                            fontSize: '10px',
                            width: 'fit-content',
                            padding: '2px 8px',
                            border: '1px solid rgba(5, 155, 182, 0.2)',
                          }}
                        >
                          Recently uploaded
                        </span>
                      )}
                    </div>

                    <div className='action-matrix d-flex align-items-center gap-2'>
                      {/* Preview Eye Icon - Always visible if file exists */}
                      {currentDoc.length > 0 && currentDoc[0].name && (
                        <button
                          className='btn btn-icon btn-light-primary btn-sm rounded-circle shadow-sm'
                          title={f({id: 'CLAIM.INPATIENT.DOCUMENT_PREVIEW'}) || 'Preview'}
                          onClick={(e) => {
                            e.stopPropagation()
                            setPreviewData({
                              isOpen: true,
                              fileName: currentDoc[0].name,
                              label: doc.label,
                            })
                          }}
                          style={{width: '32px', height: '32px'}}
                        >
                          <FaEye size={16} />
                        </button>
                      )}

                      {/* Reupload / Upload Buttons */}
                      {showReplace ? (
                        <button
                          className='btn btn-outline-primary btn-sm fw-bold px-3 py-1'
                          style={{fontSize: '11px', borderRadius: '6px'}}
                          onClick={(e) => {
                            e.stopPropagation()
                            const input = document.getElementById(`upload-${doc.label}`)
                            if (input) input.click()
                          }}
                        >
                          Reupload
                        </button>
                      ) : showUpload ? (
                        <button
                          className='btn btn-primary btn-sm fw-bold px-3 py-1 shadow-sm'
                          style={{fontSize: '11px', borderRadius: '6px'}}
                          onClick={(e) => {
                            e.stopPropagation()
                            const input = document.getElementById(`upload-${doc.label}`)
                            if (input) input.click()
                          }}
                        >
                          Upload Document
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* Progress Bar for this specific label */}
                {isUploading[doc.label] && uploadingLabel === doc.label && (
                  <div className='px-3 py-2 border-top'>
                    <div className='d-flex justify-content-between align-items-center mb-1'>
                      <span className='text-primary fw-bold' style={{fontSize: '10px'}}>
                        Uploading...
                      </span>
                      <span className='text-primary fw-bold' style={{fontSize: '10px'}}>
                        {uploadProgress}%
                      </span>
                    </div>
                    <div
                      className='progress'
                      style={{height: '4px', backgroundColor: 'rgba(5, 155, 182, 0.1)'}}
                    >
                      <div
                        className='progress-bar progress-bar-striped progress-bar-animated'
                        role='progressbar'
                        style={{width: `${uploadProgress}%`, backgroundColor: '#059BB6'}}
                      ></div>
                    </div>
                  </div>
                )}

                {/* File Name Display (Secondary) */}
                {currentDoc.length > 0 && currentDoc[0].name && (
                  <div className='document-files-list border-top py-2 px-3 bg-light'>
                    <div className='d-flex align-items-center gap-2'>
                      <FaPaperclip className='text-muted' size={12} />
                      <span
                        className='text-muted text-truncate'
                        style={{fontSize: '11px', maxWidth: '200px'}}
                        title={currentDoc[0].name}
                      >
                        {currentDoc[0].name}
                      </span>
                      <span
                        className={`status-badge ms-auto ${getStatusBadgeClass(
                          currentDoc[0].status
                        )}`}
                      >
                        {getDocumentStatusText(currentDoc[0])}
                      </span>
                    </div>
                  </div>
                )}

                <input
                  id={`upload-${doc.label}`}
                  type='file'
                  accept='.pdf,.jpg,.jpeg,.png'
                  style={{display: 'none'}}
                  onChange={(e) => handleFileChange(doc.label, e)}
                  disabled={isInteractionDisabled()}
                  aria-label={`Upload ${doc.label}`}
                />
              </div>
            )
          })}

        {/* Custom Label Rows */}
        {!isLoadingDocuments && customLabels.length > 0 && (
          <div
            className='section-header'
            style={{gridColumn: '1 / -1', marginTop: '16px', marginBottom: '4px'}}
          >
            <h6 className='fw-bold mb-0' style={{color: '#127289', fontSize: '13px'}}>
              {f({id: 'CLAIM.INPATIENT.CUSTOM_DOCUMENTS'})}
            </h6>
            <hr className='my-1' style={{borderColor: '#127289', opacity: 0.2}} />
          </div>
        )}
        {(isLoadingDocuments ? [] : customLabels).map((label) => {
          const files = documentFiles[label] || []
          const isRecentlyUploaded = recentlyUploadedLabels.has(label)
          const isValid = files.some((f) => f.status === 'VALID')
          const isRequestDoc = files.some(
            (f) => f.status === 'Request Document' || f.status === 'REQUEST'
          )
          const isInvalid = files.some((f) => f.status === 'INVALID')
          const isIdle = files.some((f) => f.status === 'IDLE')
          const showReplace =
            (isInvalid || (isIdle && isRecentlyUploaded)) && !isInteractionDisabled()
          const showUpload = (isRequestDoc || files.length === 0) && !isInteractionDisabled()
          const isCustomLabelDeletable =
            status === 'Pre-Registered' ||
            (status === 'Pending on IGL' && !pendingIGLSnapshot.includes(label))

          return (
            <div
              key={label}
              className={`document-item ${isInteractionDisabled() ? 'disabled' : ''}`}
            >
              <div className='document-header'>
                <div className='document-header-content'>
                  <div
                    className={`document-icon ${
                      isValid ? 'success' : isRequestDoc || isInvalid ? 'request' : 'warning'
                    }`}
                  >
                    {isValid ? (
                      <FaCheckCircle />
                    ) : isRequestDoc || isInvalid ? (
                      <FaExclamationTriangle />
                    ) : (
                      <FaExclamationCircle />
                    )}
                  </div>
                  <div className='d-flex flex-column flex-grow-1'>
                    <span className='document-text'>{label}</span>
                    {isRecentlyUploaded && (
                      <span
                        className='badge rounded-pill mt-1'
                        style={{
                          backgroundColor: 'rgba(5, 155, 182, 0.1)',
                          color: '#059BB6',
                          fontSize: '10px',
                          width: 'fit-content',
                          padding: '2px 8px',
                          border: '1px solid rgba(5, 155, 182, 0.2)',
                        }}
                      >
                        Recently uploaded
                      </span>
                    )}
                  </div>
                  <div className='action-matrix d-flex align-items-center gap-2'>
                    {files.length > 0 && files[0].name && (
                      <button
                        className='btn btn-icon btn-light-primary btn-sm rounded-circle shadow-sm'
                        title='Preview'
                        onClick={() =>
                          setPreviewData({isOpen: true, fileName: files[0].name, label})
                        }
                        style={{width: '32px', height: '32px'}}
                      >
                        <FaEye size={16} />
                      </button>
                    )}
                    {showReplace && (
                      <button
                        className='btn btn-outline-primary btn-sm fw-bold px-3 py-1'
                        style={{fontSize: '11px', borderRadius: '6px'}}
                        onClick={() => document.getElementById(`upload-${label}`)?.click()}
                      >
                        Reupload
                      </button>
                    )}
                    {showUpload && (
                      <button
                        className='btn btn-primary btn-sm fw-bold px-3 py-1 shadow-sm'
                        style={{fontSize: '11px', borderRadius: '6px'}}
                        onClick={() => document.getElementById(`upload-${label}`)?.click()}
                      >
                        Upload Document
                      </button>
                    )}
                    {isCustomLabelDeletable && !isInteractionDisabled() && (
                      <button
                        className='btn btn-icon btn-sm btn-light-danger rounded-circle'
                        title='Delete custom document'
                        onClick={() => handleDeleteCustomLabel(label)}
                        style={{width: '32px', height: '32px'}}
                      >
                        <FaTimes size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {files.length > 0 && files[0].name && (
                <div className='document-files-list border-top py-2 px-3 bg-light'>
                  <div className='d-flex align-items-center gap-2'>
                    <FaPaperclip className='text-muted' size={12} />
                    <span
                      className='text-muted text-truncate'
                      style={{fontSize: '11px', maxWidth: '200px'}}
                      title={files[0].name}
                    >
                      {files[0].name}
                    </span>
                    <span
                      className={`status-badge ms-auto ${getStatusBadgeClass(files[0].status)}`}
                    >
                      {getDocumentStatusText(files[0])}
                    </span>
                  </div>
                </div>
              )}
              <input
                id={`upload-${label}`}
                type='file'
                accept='.pdf,.jpg,.jpeg,.png'
                style={{display: 'none'}}
                onChange={(e) => handleFileChange(label, e)}
                disabled={isInteractionDisabled()}
              />
            </div>
          )
        })}

        {/* Other Documents from API (not in defaultDocuments or customLabels) */}
        {(isLoadingDocuments ? [] : Object.keys(documentFiles))
          .filter((label) => {
            const isDefault = defaultDocuments.some((d) => d.label === label)
            const isCustom = customLabels.includes(label)
            return !isDefault && !isCustom
          })
          .map((label) => {
            const files = documentFiles[label] || []
            const isRecentlyUploaded = recentlyUploadedLabels.has(label)
            const isValid = files.some((f) => f.status === 'VALID')
            const isRequestDoc = files.some(
              (f) => f.status === 'Request Document' || f.status === 'REQUEST'
            )
            const isInvalid = files.some((f) => f.status === 'INVALID')
            const isIdle = files.some((f) => f.status === 'IDLE')
            const showReplace =
              (isInvalid || (isIdle && isRecentlyUploaded)) && !isInteractionDisabled()
            const showUpload = (isRequestDoc || files.length === 0) && !isInteractionDisabled()

            return (
              <div
                key={label}
                className={`document-item ${isInteractionDisabled() ? 'disabled' : ''}`}
              >
                <div className='document-header'>
                  <div className='document-header-content'>
                    <div
                      className={`document-icon ${
                        isValid ? 'success' : isRequestDoc || isInvalid ? 'request' : 'warning'
                      }`}
                    >
                      {isValid ? (
                        <FaCheckCircle />
                      ) : isRequestDoc || isInvalid ? (
                        <FaExclamationTriangle />
                      ) : (
                        <FaExclamationCircle />
                      )}
                    </div>
                    <div className='d-flex flex-column flex-grow-1'>
                      <span className='document-text'>{label}</span>
                      {isRecentlyUploaded && (
                        <span
                          className='badge rounded-pill mt-1'
                          style={{
                            backgroundColor: 'rgba(5, 155, 182, 0.1)',
                            color: '#059BB6',
                            fontSize: '10px',
                            width: 'fit-content',
                            padding: '2px 8px',
                            border: '1px solid rgba(5, 155, 182, 0.2)',
                          }}
                        >
                          Recently uploaded
                        </span>
                      )}
                    </div>
                    <div className='action-matrix d-flex align-items-center gap-2'>
                      {files.length > 0 && files[0].name && (
                        <button
                          className='btn btn-icon btn-light-primary btn-sm rounded-circle shadow-sm'
                          title='Preview'
                          onClick={() =>
                            setPreviewData({isOpen: true, fileName: files[0].name, label})
                          }
                          style={{width: '32px', height: '32px'}}
                        >
                          <FaEye size={16} />
                        </button>
                      )}
                      {showReplace && (
                        <button
                          className='btn btn-outline-primary btn-sm fw-bold px-3 py-1'
                          style={{fontSize: '11px', borderRadius: '6px'}}
                          onClick={() => document.getElementById(`upload-${label}`)?.click()}
                        >
                          Reupload
                        </button>
                      )}
                      {showUpload && (
                        <button
                          className='btn btn-primary btn-sm fw-bold px-3 py-1 shadow-sm'
                          style={{fontSize: '11px', borderRadius: '6px'}}
                          onClick={() => document.getElementById(`upload-${label}`)?.click()}
                        >
                          Upload Document
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {files.length > 0 && (
                  <div className='document-files-list border-top py-2 px-3 bg-light'>
                    <div className='d-flex align-items-center gap-2'>
                      <FaPaperclip className='text-muted' size={12} />
                      {files[0].name ? (
                        <span
                          className='text-muted text-truncate'
                          style={{fontSize: '11px', maxWidth: '200px'}}
                          title={files[0].name}
                        >
                          {files[0].name}
                        </span>
                      ) : (
                        <span className='text-muted' style={{fontSize: '11px'}}>
                          No file uploaded
                        </span>
                      )}
                      <span
                        className={`status-badge ms-auto ${getStatusBadgeClass(files[0].status)}`}
                      >
                        {getDocumentStatusText(files[0])}
                      </span>
                    </div>
                  </div>
                )}
                <input
                  id={`upload-${label}`}
                  type='file'
                  accept='.pdf,.jpg,.jpeg,.png'
                  style={{display: 'none'}}
                  onChange={(e) => handleFileChange(label, e)}
                  disabled={isInteractionDisabled()}
                />
              </div>
            )
          })}

        {/* Add Custom Document Button */}
        {(status === 'Pre-Registered' || status === 'Pending on IGL') && !isInteractionDisabled() && (
          <div className='custom-label-section' style={{gridColumn: '1 / -1', padding: '8px 0'}}>
            {!allRequiredFilled && (
              <div
                className='d-flex align-items-center gap-2 px-3 py-2 mb-2'
                style={{
                  backgroundColor: '#fff9e6',
                  border: '1px solid #ffd666',
                  borderRadius: '6px',
                  color: '#ad7f00',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                <FaExclamationTriangle size={14} />
                Complete all required documents first before adding custom documents.
              </div>
            )}
            <button
              className='btn btn-outline-primary btn-sm fw-bold px-4'
              style={{
                fontSize: '12px',
                borderRadius: '6px',
                opacity: allRequiredFilled ? 1 : 0.5,
                cursor: allRequiredFilled ? 'pointer' : 'not-allowed',
              }}
              disabled={!allRequiredFilled}
              onClick={() => {
                if (allRequiredFilled) {
                  setNewCustomLabelName('')
                  setCustomLabelFile(null)
                  setShowCustomLabelModal(true)
                }
              }}
            >
              <FaPlus className='me-1' /> Add custom document
            </button>
          </div>
        )}
      </div>

      <style>
        {`
            .documents-container {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 16px;
              margin: 0 auto;
              padding: 12px;
            }

            .document-item {
              background: white;
              border-radius: 8px;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
              overflow: hidden;
              border: 1px solid #e9ecef;
              display: flex;
              flex-direction: column;
              height: 100%;
              transition: all 0.2s ease;
            }
            
            .document-item.disabled {
              background: #f8f9fa;
              opacity: 0.75;
              cursor: not-allowed;
            }

            /* Responsive adjustments */
            @media (max-width: 992px) {
              .documents-container {
                grid-template-columns: repeat(2, 1fr);
              }
            }

            @media (max-width: 576px) {
              .documents-container {
                grid-template-columns: 1fr;
              }
            }

            /* Keep the rest of your existing styles */
            .document-header {
              padding: 10px 12px;
              background: #ffffff;
              border-bottom: 1px solid #f1f3f5;
              cursor: pointer;
            }

            .document-header.disabled {
              background: #f8f9fa;
              cursor: not-allowed;
            }

            .document-header-content {
              display: flex;
              align-items: center;
              gap: 10px;
              width: 100%;
              padding: 4px;
            }

            .document-text {
              font-weight: 500;
              color: #2c3e50;
              font-size: 13px;
              flex: 1;
            }

            .document-icon {
              font-size: 16px;
              width: 24px;
              height: 24px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 6px;
            }

            .document-icon.success {
              color: #20C997;
              background: rgba(32, 201, 151, 0.1);
            }

            .document-icon.warning {
              color: #FD7E14;
              background: rgba(253, 126, 20, 0.1);
            }

            .document-icon.request {
              color: #FA5252;
              background: rgba(250, 82, 82, 0.1);
            }

            .document-files-list {
              padding: 8px 12px;
              background: white;
            }

            .document-file-item {
              display: flex;
              align-items: center;
              padding: 8px;
              border-radius: 4px;
              position: relative;
              cursor: pointer;
            }

            .document-file-item:hover {
              background-color: #f5f5f5;
            }

            .options-modal {
              position: fixed;
              background: white;
              border-radius: 4px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
              z-index: 9999;
              min-width: 120px;
              padding: 4px 0;
            }

            .options-modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              z-index: 9998;
            }

            .modal-button {
              display: block;
              width: 100%;
              padding: 8px 16px;
              border: none;
              background: none;
              text-align: left;
              cursor: pointer;
              font-size: 14px;
              color: #333;
            }

            .modal-button:hover {
              background: #f5f5f5;
            }

            .modal-button.delete {
              color: #dc3545;
            }

            .modal-button.delete:hover {
              background: #fff1f1;
            }

            .status-badge {
              padding: 3px 8px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: 500;
              display: inline-flex;
              align-items: center;
              gap: 4px;
              min-width: 70px;
              justify-content: center;
              background: #f8f9fa;
            }

            .status-badge.valid {
              background: rgba(32, 201, 151, 0.1);
              color: #20C997;
            }

            .status-badge.request {
              background: rgba(250, 82, 82, 0.1);
              color: #FA5252;
            }

            .status-badge.idle {
              background: rgba(253, 126, 20, 0.1);
              color: #FD7E14;
            }

            .document-status {
              margin-left: auto;
              padding: 4px 10px;
              border-radius: 16px;
              font-size: 12px;
              font-weight: 500;
              background: rgba(250, 82, 82, 0.1);
              color: #FA5252;
            }

            @media (max-width: 768px) {
              .documents-container {
                padding: 8px;
              }

              .document-header,
              .document-files-list {
                padding: 8px;
              }

              .document-file-item {
                padding: 8px;
                gap: 8px;
              }

              .status-badge {
                padding: 2px 6px;
                font-size: 10px;
                min-width: 60px;
              }

              .button-icon {
                width: 24px;
                height: 24px;
                font-size: 12px;
              }
              
              .button-icon-wrapper {
                gap: 6px;
                margin-left: 6px;
              }
            }

            .reupload-confirm-modal {
              position: fixed;
              // background: white;
              border-radius: 4px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
              z-index: 9999;
              min-width: 200px;
              padding: 12px;
            }

            .reupload-confirm-content {
              display: flex;
              flex-direction: column;
              gap: 12px;
            }

            .reupload-confirm-content p {
              margin: 0;
              font-size: 14px;
              color: #4a5568;
            }

            .reupload-confirm-actions {
              display: flex;
              justify-content: flex-end;
              gap: 8px;
            }

            .modal-button {
              padding: 6px 12px;
              border-radius: 4px;
              font-size: 13px;
              font-weight: 500;
              cursor: pointer;
              border: none;
              transition: all 0.2s;
            }

            .modal-button.cancel {
              background: #e9ecef;
              color: #495057;
            }

            .modal-button.cancel:hover {
              background: #dee2e6;
            }

            .modal-button.confirm {
              background: #228be6;
              color: white;
            }

            .modal-button.confirm:hover {
              background: #1c7ed6;
            }

            .modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: transparent;
              z-index: 9998;
            }

            .upload-button {
              display: flex;
              align-items: center;
              gap: 6px;
              padding: 6px 12px;
              border-radius: 4px;
              background: #f8f9fa;
              color: #495057;
              border: 1px solid #dee2e6;
              font-size: 13px;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s;
              margin-left: auto;
            }

            .upload-button:hover {
              background: #e9ecef;
              border-color: #ced4da;
            }

            .upload-button.disabled {
              background: #e9ecef;
              color: #adb5bd;
              cursor: not-allowed;
              border-color: #dee2e6;
            }
            
            .upload-button.disabled:hover {
              background: #e9ecef;
            }

            .upload-icon {
              font-size: 14px;
              color: #495057;
            }
          `}
      </style>

      {/* Reupload Confirmation Modal */}
      {/* <Modal
          show={showReuploadModal && reuploadModal.isOpen}
          onHide={closeReuploadModal}
          centered
          size='sm'
          backdrop='static'
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <div className='d-flex align-items-center'>
                <FaExclamationTriangle className='text-warning me-2' style={{fontSize: '18px'}} />
                Confirm Re-upload
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className='mb-3'>
              Are you sure you want to re-upload the document
              <span className='fw-bold d-block my-1'>{reuploadModal.documentLabel}</span>
              This action will replace the existing file.
            </p>

            <div className='d-flex justify-content-end gap-2'>
              <button className='btn btn-light' onClick={closeReuploadModal}>
                Cancel
              </button>
              <button className='btn btn-primary' onClick={handleConfirmReupload}>
                Re-upload
              </button>
            </div>
          </Modal.Body>
        </Modal> */}

      <ChatModal
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        message={chatMessage}
        claimDetails={claimDetails}
      />

      <DocumentPreviewModal
        isOpen={previewData.isOpen}
        claimNumber={claimDetails?.claimDetails?.claim?.claimNumber}
        fileName={previewData.fileName}
        label={previewData.label}
        onClose={() => setPreviewData({...previewData, isOpen: false})}
      />

      {/* Custom Label Modal */}
      <Modal show={showCustomLabelModal} onHide={() => setShowCustomLabelModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className='fs-5 fw-bold'>Add Custom Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='mb-3'>
            <label className='form-label fw-semibold required'>Document Name</label>
            <input
              type='text'
              className='form-control'
              placeholder='e.g. Additional Report'
              value={newCustomLabelName}
              onChange={(e) => setNewCustomLabelName(e.target.value)}
            />
          </div>
          <div className='mb-2'>
            <label className='form-label fw-semibold required'>File</label>
            <input
              type='file'
              className='form-control'
              accept='.pdf,.jpg,.jpeg,.png'
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) setCustomLabelFile(file)
              }}
            />
          </div>
          {customLabelFile && (
            <div className='mt-2 text-muted small'>
              <FaPaperclip className='me-1' />
              {customLabelFile.name} ({(customLabelFile.size / 1024).toFixed(1)} KB)
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className='btn btn-light fw-bold' onClick={() => setShowCustomLabelModal(false)}>
            Cancel
          </button>
          <button
            className='btn btn-primary fw-bold px-5'
            onClick={handleAddCustomLabel}
            disabled={!newCustomLabelName.trim() || !customLabelFile}
          >
            Submit
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default Documents
